import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, Type, ValueProvider } from '@angular/core';
import { Segment } from '@rci/common';
import { ContentfulServiceConfig } from '@rci/contentful-service';
import { LogServiceConfig } from '@rci/log-service';
import { ExternalScript } from '@rci/misc-service';
import { UserMonitoringServiceConfig } from '@rci/user-monitoring-service';
import { keys, pick } from 'lodash';
import { firstValueFrom, ReplaySubject } from 'rxjs';

export type EnvironmentNameResolver = (url: string) => string;

export interface AnalyticsConfiguration {
  site: {
    name: string;
  };
}

export interface CommonEnvironmentConfig {
  audienceManagerUrlTemplate: string;
  externalScripts: ExternalScript[];
  segments: Segment[];
  cookieDomain: string;
  loaderFadeOutDelay: number;
  analytics: AnalyticsConfiguration;
}

export type EnvironmentConfig = LogServiceConfig & ContentfulServiceConfig & CommonEnvironmentConfig;

export interface ConfigurableProvider extends Type<any> {
  configure: (config?: any) => ValueProvider;
}

interface ProviderConfig {
  class: ConfigurableProvider;
  config: any;
}

// replaced by DefinePlugin
declare const __GIT_HASH__: string;
declare const __BASE_HREF__: string;
declare const __DEPLOY_URL__: string;

export type ProviderConfigs = ProviderConfig[];
export type EnvironmentConfigurator<T extends EnvironmentConfig> = (environmentConfig: T) => ProviderConfigs;

@Injectable({ providedIn: 'root' })
export class EnvironmentLoaderServiceConfig {
  commonLibraryServicesConfigTypes: Type<any>[] = [
    LogServiceConfig,
    ContentfulServiceConfig,
    UserMonitoringServiceConfig,
  ];
  additionalConfigTypes: Type<any>[] = [];
}

@Injectable({ providedIn: 'root' })
export class EnvironmentLoaderService<T extends EnvironmentConfig> {
  private readonly _environmentConfigLoaded$ = new ReplaySubject<boolean>(1);
  constructor(
    private readonly httpClient: HttpClient,
    public injector: Injector,
    public config: EnvironmentLoaderServiceConfig,
  ) {}

  static defaultEnvironmentNameResolver(_url: string): string {
    return 'prod';
  }

  get environmentConfigLoaded$() {
    return this._environmentConfigLoaded$.asObservable();
  }

  load(
    environmentConfigurator: EnvironmentConfigurator<T>,
    onError?: (error: any) => void,
    environmentNameResolver?: EnvironmentNameResolver,
  ): Promise<T> {
    const hashString: string = __GIT_HASH__;
    const baseHref = __BASE_HREF__;
    const deployUrl = __DEPLOY_URL__;
    const hash = hashString ? `${hashString.substring(0, 8)}.` : '';
    const serverBaseUrl = !!deployUrl ? deployUrl : baseHref;

    const url = location.href;
    const environmentName = (environmentNameResolver || EnvironmentLoaderService.defaultEnvironmentNameResolver)(url);

    return firstValueFrom(this.httpClient.get<T>(`${serverBaseUrl}environment.${hash}${environmentName}.json`)).then(
      environmentConfig => {
        this.config.commonLibraryServicesConfigTypes
          .concat(this.config.additionalConfigTypes)
          .forEach(serviceConfigType => {
            const defaults = this.injector.get(serviceConfigType);
            Object.assign(defaults, pick(environmentConfig, keys(defaults)));
          });
        this.configure(environmentConfigurator(environmentConfig), onError);
        this._environmentConfigLoaded$.next(true);
        return environmentConfig;
      },
    );
  }

  private configure(
    providerConfigs: ProviderConfigs,
    onError: (error: any) => void,
  ): (ValueProvider | ProviderConfig)[] {
    return providerConfigs.map(providerConfig => {
      try {
        const provider = providerConfig.class.configure(providerConfig.config);
        return Object.assign(this.injector.get<any>(provider.provide), provider.useValue);
      } catch (e) {
        onError(e);
        return providerConfig;
      }
    });
  }
}
